import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { APP_VERSION } from '../utils/constants'
import './Auth.css'

const Login = () => {
  const [formData, setFormData] = useState({
    mobile: '',
    otp: ''
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [otpSent, setOtpSent] = useState(false)
  const [sendingOTP, setSendingOTP] = useState(false)
  const { loginWithOTP, generateOTP } = useAuth()
  const navigate = useNavigate()

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'mobile') {
      const sanitized = value.replace(/\D/g, '').slice(0, 10);
      setFormData({
        ...formData,
        [name]: sanitized
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    setError('')
  }

  const handleSendOTP = async () => {
    setError('')
    if (!formData.mobile.trim() || formData.mobile.length !== 10) {
      setError('Please enter a valid 10-digit mobile number')
      return
    }

    setSendingOTP(true)
    try {
      const result = await generateOTP(formData.mobile)
      if (result.success) {
        setOtpSent(true)
        if (result.data?.otp && process.env.NODE_ENV === 'development') {
          setError(`OTP sent! (Dev - OTP: ${result.data.otp})`)
        } else {
          setError('OTP sent to your mobile number!')
          setTimeout(() => setError(''), 3000)
        }
      } else {
        setError(result.message || 'Failed to send OTP')
      }
    } catch (error) {
      setError('An unexpected error occurred.')
    } finally {
      setSendingOTP(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!formData.mobile.trim()) {
      setError('Please enter your mobile number')
      return
    }

    if (!otpSent) {
      await handleSendOTP()
      return
    }

    if (!formData.otp.trim()) {
      setError('Please enter the OTP')
      return
    }

    setLoading(true)
    try {
      const result = await loginWithOTP(formData.mobile, formData.otp)
      if (result.success) {
        const { user } = result
        if (user?.role === 'admin' || user?.role === 'owner') {
          navigate('/admin', { replace: true })
        } else if (user?.role === 'franchise') {
          navigate('/franchise/dashboard', { replace: true })
        } else if (user?.role === 'franchise_tags') {
          navigate('/franchise/tags-dashboard', { replace: true })
        } else {
          navigate('/profile', { replace: true })
        }
      } else {
        setError(result.message || 'Invalid OTP. Please try again.')
        setLoading(false)
      }
    } catch (error) {
      setError('An unexpected error occurred.')
      setLoading(false)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-logo-container">
            <div className="logo-text-v2 auth-logo-adjust">
              <span className="logo-part-nah">NAH</span>
              <span className="logo-part-44">44</span>
            </div>
          </div>
          <h2>Login to NAH44</h2>
          <p className="auth-subtitle">Verify your mobile number to access your secure dashboard.</p>

          {error && (
            <div className={`auth-alert ${error.includes('OTP sent') ? 'alert-success' : 'alert-danger'}`}>
              <span>{error.includes('OTP sent') ? '✅' : '⚠️'}</span>
              <span>{error}</span>
            </div>
          )}

          {loading && !error && (
            <div className="auth-alert alert-loading">
              <div className="auth-spinner"></div>
              <span>Authenticating...</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="mobile">Mobile Number</label>
              <div className="input-container mobile-input-wrapper">
                <span className="country-code">+91</span>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="9999999999"
                  disabled={loading || otpSent}
                />
              </div>
            </div>
            {otpSent && (
              <div className="form-group slide-up">
                <label htmlFor="otp">Enter 6-Digit OTP</label>
                <div className="input-container">
                  <input
                    type="text"
                    id="otp"
                    name="otp"
                    className="otp-input"
                    value={formData.otp}
                    onChange={handleChange}
                    placeholder="000000"
                    maxLength="6"
                    disabled={loading}
                  />
                </div>
                <div className="resend-box">
                  <button
                    type="button"
                    className="btn-resend"
                    onClick={handleSendOTP}
                    disabled={sendingOTP || loading}
                  >
                    {sendingOTP ? 'Sending...' : 'Didn\'t receive code? Resend'}
                  </button>
                </div>
              </div>
            )}

            <div className="auth-actions">
              <button
                type="submit"
                className="btn-auth"
                disabled={loading || sendingOTP}
              >
                {loading ? (
                  <div className="auth-spinner"></div>
                ) : (
                  <span>{otpSent ? 'Verify & Login' : 'Send OTP'}</span>
                )}
              </button>
            </div>
          </form>

          <div className="auth-footer">
            Don't have an account? <Link to="/register">Register now</Link>
          </div>
          
          <div style={{
            textAlign: 'center',
            fontSize: '0.75rem',
            color: 'var(--text-secondary)',
            opacity: 0.5,
            marginTop: '1rem'
          }}>
            v{APP_VERSION}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
